import React, { useState, useEffect } from 'react';
import { X, CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './button';

/**
 * Componente profesional para selección de rango de fechas
 * Solo selección visual mediante calendario
 */
const ProfessionalDateRange = ({
  startDate = '',
  endDate = '',
  onChange,
  disabled = false,
  className = '',
  label = 'Rango de Fechas'
}) => {
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [localStartDate, setLocalStartDate] = useState(startDate);
  const [localEndDate, setLocalEndDate] = useState(endDate);
  const [displayMonth, setDisplayMonth] = useState(new Date().getMonth());
  const [displayYear, setDisplayYear] = useState(new Date().getFullYear());

  // Sincronizar con props externas
  useEffect(() => {
    setLocalStartDate(startDate);
    setLocalEndDate(endDate);
  }, [startDate, endDate]);

  // Cuando se abre el calendario, navegar al mes de la fecha de inicio si existe
  useEffect(() => {
    if (isCalendarOpen && localStartDate) {
      const dateMatch = localStartDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (dateMatch) {
        const [, year, month] = dateMatch;
        setDisplayYear(parseInt(year, 10));
        setDisplayMonth(parseInt(month, 10) - 1); // Los meses son 0-indexed
      }
    } else if (isCalendarOpen && !localStartDate) {
      // Si no hay fecha seleccionada, mostrar el mes actual
      const today = new Date();
      setDisplayMonth(today.getMonth());
      setDisplayYear(today.getFullYear());
    }
  }, [isCalendarOpen, localStartDate]);

  // Formatear fecha para mostrar (dd/mm/yyyy)
  // Corrige problemas de timezone usando fecha local directamente
  const formatDateForDisplay = (dateStr) => {
    if (!dateStr) return '';
    
    // Si es formato YYYY-MM-DD, parsearlo directamente sin timezone
    const dateMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dateMatch) {
      const [, year, month, day] = dateMatch;
      return `${day}/${month}/${year}`;
    }
    
    // Fallback para otros formatos
    const date = new Date(dateStr);
    if (isNaN(date)) return dateStr;
    
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Obtener fecha de hoy como string (timezone local directo)
  const getTodayString = () => {
    const now = new Date();
    // Usar directamente los métodos locales sin conversiones UTC
    const year = now.getFullYear();
    const month = (now.getMonth() + 1).toString().padStart(2, '0');
    const day = now.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Generar opciones de días para el calendario
  const generateCalendarDays = () => {
    // Usar el mes y año que se está visualizando
    const firstDay = new Date(displayYear, displayMonth, 1);
    const lastDay = new Date(displayYear, displayMonth + 1, 0);
    
    // Obtener el día de la semana del primer día (0 = domingo, 1 = lunes, etc.)
    const startDay = (firstDay.getDay() + 6) % 7; // Convertir para que lunes sea 0
    
    const days = [];
    
    // Días del mes anterior (grises)
    for (let i = startDay - 1; i >= 0; i--) {
      const date = new Date(displayYear, displayMonth, -i);
      days.push({ date, isCurrentMonth: false });
    }
    
    // Días del mes actual
    for (let day = 1; day <= lastDay.getDate(); day++) {
      const date = new Date(displayYear, displayMonth, day);
      days.push({ date, isCurrentMonth: true });
    }
    
    // Completar la grilla con días del siguiente mes
    const totalCells = Math.ceil(days.length / 7) * 7;
    let nextMonthDay = 1;
    while (days.length < totalCells) {
      const date = new Date(displayYear, displayMonth + 1, nextMonthDay);
      days.push({ date, isCurrentMonth: false });
      nextMonthDay++;
    }
    
    return days;
  };

  // Navegar al mes anterior
  const goToPreviousMonth = () => {
    if (displayMonth === 0) {
      setDisplayMonth(11);
      setDisplayYear(displayYear - 1);
    } else {
      setDisplayMonth(displayMonth - 1);
    }
  };

  // Navegar al mes siguiente
  const goToNextMonth = () => {
    const today = new Date();
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();
    
    // No permitir navegar a meses futuros
    if (displayYear > currentYear || (displayYear === currentYear && displayMonth >= currentMonth)) {
      return;
    }
    
    if (displayMonth === 11) {
      setDisplayMonth(0);
      setDisplayYear(displayYear + 1);
    } else {
      setDisplayMonth(displayMonth + 1);
    }
  };

  // Convertir fecha a string local (sin timezone)
  const dateToLocalString = (date) => {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Verificar si una fecha está en el rango seleccionado
  const isDateInRange = (date) => {
    const dateStr = dateToLocalString(date);
    const start = localStartDate;
    const end = localEndDate;
    
    if (!start || !end) return false;
    return dateStr >= start && dateStr <= end;
  };

  // Verificar si una fecha es el inicio o fin del rango
  const isDateEdge = (date, type) => {
    const dateStr = dateToLocalString(date);
    return type === 'start' ? dateStr === localStartDate : dateStr === localEndDate;
  };



  // Presets rápidos
  const presets = [
    { 
      label: 'Hoy', 
      getValue: () => {
        const today = getTodayString();
        return [today, today];
      }
    },
          { 
        label: 'Ayer', 
        getValue: () => {
          const yesterday = new Date();
          yesterday.setDate(yesterday.getDate() - 1);
          const dateStr = dateToLocalString(yesterday);
          return [dateStr, dateStr];
        }
      },
      { 
        label: 'Últimos 7 días', 
        getValue: () => {
          const today = getTodayString();
          const weekAgo = new Date();
          weekAgo.setDate(weekAgo.getDate() - 6);
          return [dateToLocalString(weekAgo), today];
        }
      },
      { 
        label: 'Últimos 30 días', 
        getValue: () => {
          const today = getTodayString();
          const monthAgo = new Date();
          monthAgo.setDate(monthAgo.getDate() - 29);
          return [dateToLocalString(monthAgo), today];
        }
      },
      { 
        label: 'Este mes', 
        getValue: () => {
          const now = new Date();
          const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
          const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
          return [
            dateToLocalString(firstDay),
            dateToLocalString(lastDay)
          ];
        }
      }
  ];

  // Aplicar preset
  const applyPreset = (preset) => {
    const [start, end] = preset.getValue();
    setLocalStartDate(start);
    setLocalEndDate(end);
    onChange && onChange(start, end);
    setIsCalendarOpen(false);
  };

  // Limpiar fechas
  const clearDates = () => {
    setLocalStartDate('');
    setLocalEndDate('');
    onChange && onChange('', '');
  };

  const calendarDays = generateCalendarDays();
  const monthNames = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];
  const weekDays = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

  return (
    <div className={`relative ${className}`}>
      {/* Label */}
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label}
        </label>
      )}

      {/* Selector Container */}
      <div className="relative">
        <div
          onClick={() => setIsCalendarOpen(!isCalendarOpen)}
          className="w-full flex items-center justify-between p-4 bg-white border border-gray-300 rounded-lg shadow-sm hover:border-blue-400 hover:shadow-md focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all duration-200 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
        >
          {/* Date Display */}
          <div className="flex items-center space-x-4 flex-1">
            {/* Start Date */}
            <div className="flex flex-col items-start">
              <span className="text-xs font-medium text-gray-500 mb-1">Desde</span>
              <span className="text-sm font-medium text-gray-900">
                {formatDateForDisplay(localStartDate) || 'Seleccionar fecha'}
              </span>
            </div>

            {/* Separator */}
            <div className="flex items-center">
              <div className="w-6 h-px bg-gray-300"></div>
              <div className="w-1 h-1 bg-gray-400 rounded-full mx-1"></div>
              <div className="w-6 h-px bg-gray-300"></div>
            </div>

            {/* End Date */}
            <div className="flex flex-col items-start">
              <span className="text-xs font-medium text-gray-500 mb-1">Hasta</span>
              <span className="text-sm font-medium text-gray-900">
                {formatDateForDisplay(localEndDate) || 'Seleccionar fecha'}
              </span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center space-x-2 ml-4">
            {(localStartDate || localEndDate) && (
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  clearDates();
                }}
                disabled={disabled}
                variant="ghost"
                size="sm"
                className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
              >
                <X className="w-4 h-4" />
              </Button>
            )}
            
            <div className="p-1.5 text-blue-500">
              <CalendarIcon className="w-5 h-5" />
            </div>
          </div>
        </div>
      </div>

      {/* Calendar Dropdown */}
      {isCalendarOpen && (
        <>
          <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-xl shadow-xl z-50 p-6">
            
            {/* Presets */}
            <div className="flex flex-wrap gap-2 mb-4 pb-4 border-b border-gray-200">
              {presets.map((preset, index) => (
                <Button
                  key={index}
                  onClick={() => applyPreset(preset)}
                  variant="outline"
                  size="sm"
                  className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  {preset.label}
                </Button>
              ))}
            </div>

            {/* Calendar Header */}
            <div className="flex items-center justify-between mb-4">
              <Button
                onClick={goToPreviousMonth}
                variant="ghost"
                size="sm"
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                disabled={disabled}
              >
                <ChevronLeft className="w-5 h-5 text-gray-600" />
              </Button>
              <h3 className="text-sm font-semibold text-gray-900">
                {monthNames[displayMonth]} {displayYear}
              </h3>
              <Button
                onClick={goToNextMonth}
                variant="ghost"
                size="sm"
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={disabled || (() => {
                  const today = new Date();
                  const currentMonth = today.getMonth();
                  const currentYear = today.getFullYear();
                  return displayYear > currentYear || (displayYear === currentYear && displayMonth >= currentMonth);
                })()}
              >
                <ChevronRight className="w-5 h-5 text-gray-600" />
              </Button>
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-1 mb-4">
              {/* Week days header */}
              {weekDays.map(day => (
                <div key={day} className="p-2 text-xs font-medium text-gray-500 text-center">
                  {day}
                </div>
              ))}

              {/* Calendar days */}
              {calendarDays.map((dayObj, index) => {
                const { date, isCurrentMonth } = dayObj;
                const dateStr = dateToLocalString(date);
                const isToday = dateStr === getTodayString();
                const isSelected = dateStr === localStartDate || dateStr === localEndDate;
                const isInRange = isDateInRange(date);
                const isStartEdge = isDateEdge(date, 'start');
                const isEndEdge = isDateEdge(date, 'end');
                const isFuture = date > new Date();

                return (
                  <Button
                    key={index}
                    onClick={() => {
                      if (isFuture) return;
                      
                      if (!localStartDate || (localStartDate && localEndDate)) {
                        // Empezar nueva selección
                        setLocalStartDate(dateStr);
                        setLocalEndDate('');
                        onChange && onChange(dateStr, '');
                      } else if (dateStr >= localStartDate) {
                        // Establecer fecha fin
                        setLocalEndDate(dateStr);
                        onChange && onChange(localStartDate, dateStr);
                      } else {
                        // Nueva fecha de inicio (menor que la actual)
                        setLocalStartDate(dateStr);
                        setLocalEndDate('');
                        onChange && onChange(dateStr, '');
                      }
                    }}
                    disabled={isFuture}
                    variant="ghost"
                    size="sm"
                    className={`
                      p-2 text-sm relative transition-all duration-150 min-h-[36px] w-full
                      ${!isCurrentMonth ? 'text-gray-300' : 'text-gray-700'}
                      ${isToday && isCurrentMonth ? 'font-bold text-blue-600 bg-blue-50' : ''}
                      ${isSelected ? 'bg-blue-600 text-white font-semibold shadow-sm' : ''}
                      ${isInRange && !isSelected ? 'bg-blue-100 text-blue-700' : ''}
                      ${isStartEdge && !isSelected ? 'bg-blue-200 text-blue-800 rounded-l-lg' : ''}
                      ${isEndEdge && !isSelected ? 'bg-blue-200 text-blue-800 rounded-r-lg' : ''}
                      ${isFuture ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-100'}
                      ${!isCurrentMonth ? 'opacity-30' : ''}
                    `}
                  >
                    {date.getDate()}
                  </Button>
                );
              })}
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-2 pt-4 border-t border-gray-200">
              <Button
                onClick={() => setIsCalendarOpen(false)}
                variant="outline"
                size="sm"
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
              >
                Cancelar
              </Button>
              <Button
                onClick={() => setIsCalendarOpen(false)}
                size="sm"
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Aplicar
              </Button>
            </div>
          </div>

          {/* Overlay */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsCalendarOpen(false)}
          />
        </>
      )}
    </div>
  );
};

export default ProfessionalDateRange;